import { datadogLogs } from '@datadog/browser-logs';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useContext, useEffect } from 'react';
import { toAccountAllocationGridReport } from '../editAccountAllocations/accountAllocationGridReport';
import { useEditAccountAllocations } from '../editAccountAllocations/hooks/useEditAccountAllocations';
import {
  readStagedAccountAllocations,
  useStagedAccountAllocations,
} from '../editAccountAllocations/hooks/useStagedAccountAllocations';
import { decideAccountAllocationReview } from '../editAccountAllocations/reviewAccountAllocationsModel';

// Reads only: the report the editor loads and the staging it left behind.
// Nothing here stages, solves or writes.
export const useReviewAccountAllocations = (
  householdClientGroupId: string | undefined,
) => {
  const { workflowData } = useContext(WorkflowContext);
  const { report, loading, error, refetch } = useEditAccountAllocations(
    householdClientGroupId,
  );
  const { state } = useStagedAccountAllocations({
    householdClientGroupId,
    report,
  });

  // Undecided until the report arrives: deciding on LOADING would flash a
  // "nothing staged" block before the staging can be read.
  const decision =
    report && state.status !== 'LOADING'
      ? decideAccountAllocationReview(state, report)
      : undefined;
  const blockCode = decision?.status === 'BLOCKED' ? decision.code : undefined;

  useEffect(() => {
    if (!error) return;
    datadogLogs.logger.error(
      'Review account allocations load failed',
      { clientGroupId: householdClientGroupId },
      error,
    );
  }, [error, householdClientGroupId]);

  useEffect(() => {
    if (!blockCode) return;
    datadogLogs.logger.warn('Review account allocations blocked', {
      clientGroupId: householdClientGroupId,
      code: blockCode,
    });
  }, [blockCode, householdClientGroupId]);

  const retry = () => {
    refetch().catch((retryError: unknown) => {
      datadogLogs.logger.error(
        'Review account allocations retry failed',
        { clientGroupId: householdClientGroupId },
        retryError instanceof Error ? retryError : undefined,
      );
    });
  };

  // The report was read when the review opened, and the household can change
  // while it stays open. Approve decides again on a fresh one, so nothing goes
  // to signature that the apply step would refuse. The refetch also refreshes
  // the review, so a block shows in place of advancing; a failed one leaves
  // the unavailable state with Retry.
  const approve = async (advance: () => void | Promise<void>) => {
    const fresh = await refetch().then(
      ({ data }) =>
        toAccountAllocationGridReport(
          data.fetchPortfolioReports.portfolioReports[0],
        ),
      () => undefined,
    );
    if (!fresh) return;
    const freshState = readStagedAccountAllocations(
      workflowData,
      householdClientGroupId,
      fresh.id,
    );
    if (freshState.status === 'LOADING') return;
    const freshDecision = decideAccountAllocationReview(freshState, fresh);
    if (freshDecision.status !== 'REVIEWABLE') return;
    await advance();
  };

  return { report, decision, loading, error, retry, approve };
};
